<template>
  <div class="admin-page">
    <!-- ── 页头 ───────────────────────────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>敏感词管理</h1>
        <p>{{ scopeHint }}</p>
      </div>
      <div class="head-actions">
        <el-button type="primary" class="primary-btn" icon="el-icon-plus" @click="handleCreate">
          新增敏感词
        </el-button>
      </div>
    </div>

    <!-- ── 词库分段：全站 / 学校，两类词库的入口 ─────────────── -->
    <div class="status-tabs lib-tabs">
      <button
        v-for="tab in scopeTabs"
        :key="tab.value"
        type="button"
        class="tab"
        :class="{ 'is-active': listQuery.scope === tab.value }"
        @click="switchScope(tab.value)"
      >
        {{ tab.label }}<span class="tab-count">{{ tab.count }}</span>
      </button>
    </div>

    <!-- ── 筛选 ───────────────────────────────────────────── -->
    <div class="filter-bar">
      <el-input
        v-model="listQuery.keyword"
        placeholder="搜索敏感词"
        clearable
        prefix-icon="el-icon-search"
        class="search-input"
        @keyup.enter.native="handleFilter"
        @clear="handleFilter"
      />
      <el-select
        v-if="listQuery.scope !== 'ALL'"
        v-model="listQuery.schoolId"
        placeholder="全部学校"
        clearable
        class="school-select"
        @change="handleFilter"
      >
        <el-option v-for="item in schoolOptions" :key="item.id" :label="item.name" :value="item.id" />
      </el-select>
      <el-select v-model="listQuery.status" placeholder="全部状态" clearable class="school-select" @change="handleFilter">
        <el-option label="启用" :value="1" />
        <el-option label="禁用" :value="0" />
      </el-select>
      <el-select
        v-model="listQuery.category"
        placeholder="全部类型"
        clearable
        class="school-select"
        @change="handleFilter"
      >
        <el-option
          v-for="(label, value) in categoryOptions"
          :key="value"
          :label="label"
          :value="value"
        />
      </el-select>
      <el-button type="primary" class="primary-btn" @click="handleFilter">查询</el-button>
      <el-button class="ghost-btn" @click="resetFilter">重置</el-button>
      <div class="filter-tail">共 <b>{{ total }}</b> 个</div>
    </div>

    <!-- ── 列表 ───────────────────────────────────────────── -->
    <div class="table-card">
      <el-table v-loading="listLoading" :data="list" style="width: 100%">
        <el-table-column prop="id" label="ID" width="80" align="center" />

        <el-table-column label="敏感词" min-width="200">
          <template slot-scope="{ row }">
            <span class="tag is-rose word-chip">{{ row.word }}</span>
          </template>
        </el-table-column>

        <!-- 违规类型：决定「哪些字段会查这个词」。联系方式类字段跳过导流广告，
             所以这一列不是装饰 —— 运营要能一眼看出这条词管不管联系方式。 -->
        <el-table-column label="违规类型" width="120">
          <template slot-scope="{ row }">
            <span class="cchip" :class="'cchip--' + (row.category || 'OTHER').toLowerCase()">
              {{ categoryLabel(row.category) }}
            </span>
          </template>
        </el-table-column>

        <!-- 这一列是列表的信息重心：这个词对谁生效，扫一眼就要分清 -->
        <el-table-column label="适用范围" width="200">
          <template slot-scope="{ row }">
            <span v-if="row.scope === 'ALL'" class="schip schip--all">全站</span>
            <span v-else class="schip">{{ row.schoolName || ('已下线学校#' + row.schoolId) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="110">
          <template slot-scope="{ row }">
            <span class="status-pill" :class="row.status === 1 ? 's1' : 's0'">
              <i />{{ row.status === 1 ? '启用' : '禁用' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="更新时间" width="170">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ formatDate(row.updatedAt || row.createdAt) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="140" fixed="right">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <!-- 全站词对管理员 / 运营是只读参考：让他们知道平台已经拦了哪些词，
                   免得重复录；但不给操作入口（后端写接口也会拦） -->
              <span v-if="!row.manageable" class="act act-disabled">只读</span>
              <el-dropdown v-else trigger="click" @command="cmd => handleAction(cmd, row)">
                <button type="button" class="act act-plain act-more">
                  操作<i class="el-icon-arrow-down" />
                </button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item v-if="row.status === 0" command="enable">
                    <i class="el-icon-check" /> 启用
                  </el-dropdown-item>
                  <el-dropdown-item v-else command="disable">
                    <i class="el-icon-close" /> 禁用
                  </el-dropdown-item>
                  <el-dropdown-item command="edit">
                    <i class="el-icon-edit" /> 编辑
                  </el-dropdown-item>
                  <el-dropdown-item command="delete" divided>
                    <i class="el-icon-delete" /> 删除
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <el-pagination
          v-show="total > 0"
          background
          :current-page="listQuery.page"
          :page-size="listQuery.size"
          :page-sizes="[10, 20, 50]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <!-- ── 新增 / 编辑 ─────────────────────────────────────── -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="620px"
      :close-on-click-modal="false"
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="92px">
        <el-form-item label="敏感词" prop="word">
          <el-input
            v-model="form.word"
            maxlength="100"
            show-word-limit
            placeholder="命中该词的纸条与动态会被拦截"
          />
        </el-form-item>

        <!-- 违规类型：决定这个字段"管不管联系方式"。
             导流广告类在联系方式字段上不校验（否则「加微信」这种正常填写全被拦死），
             其余类型在所有字段都校验 —— 这条说明必须写在界面上，不然运营会以为漏配了。 -->
        <el-form-item label="违规类型" prop="category">
          <el-select v-model="form.category" placeholder="请选择违规类型" style="width: 100%">
            <el-option
              v-for="(label, value) in categoryOptions"
              :key="value"
              :label="label"
              :value="value"
            />
          </el-select>
          <div class="field-note">
            <i class="el-icon-info" />
            <template v-if="form.category === 'AD'">
              导流广告在「联系方式」字段上<strong>不拦截</strong>（该字段本就是留微信/QQ 的），其余正文场景照拦。
            </template>
            <template v-else>
              该类型在<strong>正文与联系方式</strong>上都会被拦截。若这个词只是导流广告（加微信、加群等），请选「导流广告」。
            </template>
          </div>
        </el-form-item>

        <template v-if="!isEdit">
          <!-- 适用范围：不可撤回的归属，做成两张卡而不是下拉，防手滑 -->
          <el-form-item label="适用范围" prop="scope">
            <div class="scope-cards">
              <div
                class="scope-card"
                :class="{ 'scope-card--on': form.scope === 'ALL', 'scope-card--off': !isSuperAdmin }"
                @click="chooseScope('ALL')"
              >
                <div class="scope-card__t">
                  <span class="scope-card__dot" />全站
                </div>
                <div class="scope-card__d">
                  {{ isSuperAdmin ? '所有学校的用户都校验' : '仅超级管理员可用' }}
                </div>
              </div>
              <div
                class="scope-card"
                :class="{ 'scope-card--on': form.scope === 'SCHOOL' }"
                @click="chooseScope('SCHOOL')"
              >
                <div class="scope-card__t">
                  <span class="scope-card__dot" />指定学校
                </div>
                <div class="scope-card__d">只对选中的学校生效，可多选</div>
              </div>
            </div>
          </el-form-item>

          <el-form-item label="适用学校" v-if="form.scope === 'SCHOOL'">
            <div class="school-picker">
              <div class="school-grid" v-if="schoolOptions.length">
                <div
                  v-for="item in schoolOptions"
                  :key="item.id"
                  class="sitem"
                  :class="{ 'sitem--on': isSchoolSelected(item.id) }"
                  @click="toggleSchool(item.id)"
                >
                  <span class="sbox">{{ isSchoolSelected(item.id) ? '✓' : '' }}</span>
                  <span class="sitem__n">{{ item.name }}</span>
                </div>
              </div>
              <div class="school-empty" v-else>当前账号没有可管理的学校，请联系超级管理员授权。</div>

              <div class="picker-bar" v-if="schoolOptions.length">
                <span>已选 <b>{{ form.schoolIds.length }}</b> 所</span>
                <span class="picker-bar__acts">
                  <el-button type="text" @click="selectAllSchools">全选我的学校</el-button>
                  <el-button type="text" @click="form.schoolIds = []">清空</el-button>
                </span>
              </div>
            </div>
          </el-form-item>
        </template>

        <!-- 编辑：归属锁定。它决定了这条词归谁管，改它等于跨辖区搬词 -->
        <el-form-item label="适用范围" v-else>
          <div class="scope-locked">
            <span v-if="form.scope === 'ALL'" class="schip schip--all">全站</span>
            <span v-else class="schip">{{ form.schoolName }}</span>
          </div>
          <div class="field-note">
            <i class="el-icon-lock" /> 适用范围不可修改。要给别的学校加同一个词，请单独新增。
          </div>
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button class="ghost-btn" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" class="primary-btn" :loading="submitting" @click="submitForm">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  getSensitiveWordList,
  createSensitiveWord,
  updateSensitiveWord,
  deleteSensitiveWord,
  getSensitiveWordCategories
} from '@/api/sensitive-word'

export default {
  name: 'SensitiveWord',
  data() {
    return {
      list: [],
      total: 0,
      globalTotal: 0,
      schoolTotal: 0,
      listLoading: false,
      submitting: false,
      listQuery: {
        page: 1,
        size: 10,
        keyword: '',
        status: null,
        // 空字符串 = 全部；顶部两个分段切换时写 ALL / SCHOOL
        scope: '',
        schoolId: '',
        // 空 = 全部类型
        category: ''
      },
      dialogVisible: false,
      dialogTitle: '',
      isEdit: false,
      // 违规类型字典由后端下发（/admin/sensitive-word/categories）——
      // 前端不写死一份文案，否则新增一个类型要改两个仓库，漏一处就显示成英文 code
      categoryOptions: {},
      form: {
        id: null,
        word: '',
        scope: 'SCHOOL',
        schoolIds: [],
        schoolName: '',
        category: 'ABUSE',
        status: 1
      },
      rules: {
        word: [
          { required: true, message: '请输入敏感词', trigger: 'blur' },
          { min: 1, max: 100, message: '长度在 1 到 100 个字符', trigger: 'blur' }
        ],
        category: [
          { required: true, message: '请选择违规类型', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    ...mapGetters(['isSuperAdmin', 'schoolOptions']),

    /** 页头那句说明 —— 明确告诉他这次能维护到哪一层，免得以为系统少了数据 */
    scopeHint() {
      if (this.isSuperAdmin) {
        return '全站词库对所有学校生效；学校词库只对选中的学校生效'
      }
      if (!this.schoolOptions.length) {
        return '当前账号还没有被授权任何学校，只能查看全站词库'
      }
      return '当前账号可维护已授权 ' + this.schoolOptions.length + ' 所学校的词库；全站词库只读'
    },

    /** 分段计数由后端按同一套筛选条件下发，前端不自己数 */
    scopeTabs() {
      return [
        { value: '', label: '全部', count: this.globalTotal + this.schoolTotal },
        { value: 'ALL', label: '全站词库', count: this.globalTotal },
        { value: 'SCHOOL', label: '学校词库', count: this.schoolTotal }
      ]
    }
  },
  created() {
    this.loadCategories()
    this.getList()
  },
  methods: {
    /** 类型字典：只取一次，失败时退回英文 code，页面不至于空白 */
    loadCategories() {
      getSensitiveWordCategories().then(res => {
        this.categoryOptions = (res && res.data) || {}
      }).catch(() => {})
    },

    categoryLabel(value) {
      if (!value) return '其他'
      return this.categoryOptions[value] || value
    },

    getList() {
      this.listLoading = true
      getSensitiveWordList(this.listQuery).then(res => {
        const data = res.data || {}
        this.list = data.list || []
        this.total = data.total || 0
        this.globalTotal = data.globalTotal || 0
        this.schoolTotal = data.schoolTotal || 0
        this.listLoading = false
      }).catch(() => {
        this.listLoading = false
      })
    },

    switchScope(value) {
      this.listQuery.scope = value
      // 切到「全站词库」时学校筛选项没有意义，顺手清掉，免得筛出空列表让人以为没数据
      if (value === 'ALL') {
        this.listQuery.schoolId = ''
      }
      this.handleFilter()
    },

    handleFilter() {
      this.listQuery.page = 1
      this.getList()
    },

    resetFilter() {
      this.listQuery.keyword = ''
      this.listQuery.status = null
      this.listQuery.schoolId = ''
      this.listQuery.category = ''
      this.handleFilter()
    },

    handleSizeChange(val) {
      this.listQuery.size = val
      this.getList()
    },

    handleCurrentChange(val) {
      this.listQuery.page = val
      this.getList()
    },

    handleAction(cmd, row) {
      if (cmd === 'enable') return this.handleToggleStatus(row, 1)
      if (cmd === 'disable') return this.handleToggleStatus(row, 0)
      if (cmd === 'edit') return this.handleUpdate(row)
      if (cmd === 'delete') return this.handleDelete(row)
    },

    isSchoolSelected(id) {
      return this.form.schoolIds.indexOf(id) >= 0
    },

    toggleSchool(id) {
      const index = this.form.schoolIds.indexOf(id)
      if (index >= 0) {
        this.form.schoolIds.splice(index, 1)
      } else {
        this.form.schoolIds.push(id)
      }
    },

    selectAllSchools() {
      this.form.schoolIds = this.schoolOptions.map(item => item.id)
    },

    chooseScope(scope) {
      if (scope === 'ALL' && !this.isSuperAdmin) return
      this.form.scope = scope
      // 切到「指定学校」时如果一所都没选，默认把他管的学校全带上 ——
      // 运营的授权范围本来就小，全选是最常见的操作
      if (scope === 'SCHOOL' && !this.form.schoolIds.length) {
        this.selectAllSchools()
      }
    },

    resetForm() {
      this.form = {
        id: null,
        word: '',
        scope: 'SCHOOL',
        schoolIds: [],
        schoolName: '',
        category: 'ABUSE',
        status: 1
      }
      if (this.$refs.form) {
        this.$refs.form.clearValidate()
      }
    },

    handleCreate() {
      this.resetForm()
      this.isEdit = false
      this.form.scope = 'SCHOOL'
      this.selectAllSchools()
      this.dialogTitle = '新增敏感词'
      this.dialogVisible = true
    },

    handleUpdate(row) {
      this.resetForm()
      this.isEdit = true
      this.form.id = row.id
      this.form.word = row.word
      this.form.scope = row.scope
      this.form.schoolName = row.schoolName
      // 老数据可能没有 category（迁移前建的），统一落到 OTHER
      this.form.category = row.category || 'OTHER'
      this.form.status = row.status
      this.dialogTitle = '编辑敏感词'
      this.dialogVisible = true
    },

    submitForm() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        if (!this.isEdit && this.form.scope === 'SCHOOL' && !this.form.schoolIds.length) {
          this.$message.warning('请至少选择一所学校')
          return
        }

        this.submitting = true
        const request = this.isEdit
          ? updateSensitiveWord({
            id: this.form.id,
            word: this.form.word,
            category: this.form.category,
            status: this.form.status
          })
          : createSensitiveWord({
            word: this.form.word,
            scope: this.form.scope,
            category: this.form.category,
            // 全站词不传学校：范围只有一个出处，免得留下"既标了全站又挂着学校"的矛盾数据
            schoolIds: this.form.scope === 'SCHOOL' ? this.form.schoolIds : [],
            status: this.form.status
          })

        request.then(res => {
          this.$message.success((res && res.message) || (this.isEdit ? '更新成功' : '创建成功'))
          this.submitting = false
          this.dialogVisible = false
          this.getList()
        }).catch(() => {
          this.submitting = false
        })
      })
    },

    handleToggleStatus(row, status) {
      updateSensitiveWord({ id: row.id, status }).then(res => {
        this.$message.success((res && res.message) || (status === 1 ? '已启用' : '已禁用'))
        this.getList()
      }).catch(() => {})
    },

    handleDelete(row) {
      const where = row.scope === 'ALL' ? '全站' : ('「' + (row.schoolName || '该校') + '」')
      this.$confirm(
        '删除后，' + where + '的用户发布纸条与动态时将不再拦截「' + row.word + '」。',
        '删除这个敏感词？',
        { confirmButtonText: '删除', type: 'warning' }
      ).then(() => {
        deleteSensitiveWord(row.id).then(res => {
          this.$message.success((res && res.message) || '删除成功')
          this.getList()
        })
      }).catch(() => {})
    },

    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString('zh-CN', { hour12: false })
    }
  }
}
</script>

<style lang="scss" scoped>
/* 词库分段与页头之间留一口气 */
.lib-tabs {
  margin-bottom: 14px;
}

/* 敏感词本体：用玫瑰底把它从一堆灰字里提出来 */
.word-chip {
  font-size: 13px;
  font-weight: 600;
  padding: 3px 10px;
}

/* 违规类型 chip。
   只有「导流广告」用主色 —— 它是唯一在联系方式字段上不生效的类型，
   值得被一眼认出来；其余类型保持中性灰，避免整列变成调色板。 */
.cchip {
  display: inline-block;
  font-size: 11.5px;
  color: #3a2f3f;
  background: #f4f1f2;
  border-radius: 6px;
  padding: 2px 8px;
  white-space: nowrap;
}
.cchip--ad {
  color: #c93b57;
  background: #fdf1f0;
  font-weight: 600;
}

/* 适用范围 chips —— 与公告列表同一套语言 */.schip {
  display: inline-block;
  font-size: 11px;
  background: #f4ece6;
  color: #3a2f3f;
  border-radius: 6px;
  padding: 2px 8px;
  white-space: nowrap;
}
.schip--all {
  background: #171219;
  color: #fff;
  font-weight: 600;
  letter-spacing: 0.02em;
}

/* 只读标记：全站词对管理员 / 运营不给操作入口 */
.act-disabled {
  color: #a79ba9;
  cursor: not-allowed;
}

/* 适用范围单选卡 */
.scope-cards {
  display: flex;
  gap: 10px;
}
.scope-card {
  flex: 1;
  border: 1.5px solid #e8e0e6;
  border-radius: 10px;
  padding: 12px 14px;
  background: #fcfbfa;
  cursor: pointer;
  transition: all 0.15s;
}
.scope-card--on {
  border-color: #c93b57;
  background: #fdf1f0;
}
.scope-card--off {
  opacity: 0.45;
  cursor: not-allowed;
}
.scope-card__t {
  font-size: 13px;
  font-weight: 700;
  color: #171219;
  display: flex;
  align-items: center;
  gap: 7px;
}
.scope-card__dot {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 1.5px solid #cfc6d1;
  flex-shrink: 0;
}
.scope-card--on .scope-card__dot {
  border: 4px solid #c93b57;
}
.scope-card__d {
  font-size: 11.5px;
  color: #8c8191;
  margin-top: 6px;
  line-height: 1.5;
}

/* 学校选择器：勾选网格 + 底部实时汇总 */
.school-picker {
  border: 1px solid #e8e0e6;
  border-radius: 10px;
  background: #fcfbfa;
  padding: 12px;
}
.school-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.sitem {
  display: flex;
  align-items: center;
  gap: 7px;
  background: #fff;
  border: 1px solid #e8e0e6;
  border-radius: 8px;
  padding: 7px 11px;
  font-size: 12.5px;
  color: #3a2f3f;
  cursor: pointer;
  transition: all 0.15s;
}
.sitem--on {
  border-color: #c93b57;
  background: #fdf1f0;
  font-weight: 600;
  color: #171219;
}
.sbox {
  width: 13px;
  height: 13px;
  border-radius: 3px;
  border: 1.5px solid #cfc6d1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  color: #fff;
  line-height: 1;
}
.sitem--on .sbox {
  background: #c93b57;
  border-color: #c93b57;
}
.school-empty {
  font-size: 12.5px;
  color: #8c8191;
  padding: 6px 2px;
}
.picker-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  padding-top: 11px;
  border-top: 1px dashed #e8e0e6;
  font-size: 12px;
  color: #8c8191;
}
.picker-bar b {
  color: #c93b57;
  font-weight: 700;
}
.picker-bar__acts {
  margin-left: auto;
}

/* 编辑时的只读归属 */
.scope-locked {
  display: flex;
  align-items: center;
  gap: 8px;
}
.field-note {
  font-size: 11.5px;
  color: #8c8191;
  line-height: 1.6;
  margin-top: 6px;
}
</style>
